import axiosInstance from './axios';

/**
 * API module for AI testing engagements ("Configure with AI" flow).
 * Engagements own documents, a conversation (audit trail), and versioned
 * assessment results.
 */

export async function listEngagementsForControl(controlId) {
  const resp = await axiosInstance.get(`/api/ai-engagements/control/${controlId}`);
  return resp.data?.engagements || [];
}

export async function createEngagement(payload) {
  const resp = await axiosInstance.post('/api/ai-engagements', payload);
  return resp.data;
}

export async function getEngagement(engagementId) {
  const resp = await axiosInstance.get(`/api/ai-engagements/${engagementId}`);
  return resp.data;
}

export async function updateEngagement(engagementId, payload) {
  const resp = await axiosInstance.patch(`/api/ai-engagements/${engagementId}`, payload);
  return resp.data;
}

export async function uploadEngagementDocument(engagementId, file, category, notes) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('category', category || 'evidence');
  if (notes) formData.append('notes', notes);
  const resp = await axiosInstance.post(
    `/api/ai-engagements/${engagementId}/documents`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return resp.data;
}

export async function deleteEngagementDocument(engagementId, documentId) {
  const resp = await axiosInstance.delete(`/api/ai-engagements/${engagementId}/documents/${documentId}`);
  return resp.data;
}

export async function clearEngagementMessages(engagementId) {
  const resp = await axiosInstance.delete(`/api/ai-engagements/${engagementId}/messages`);
  return resp.data;
}

export async function downloadEngagementReport(engagementId, reportKey, format = 'pdf') {
  const resp = await axiosInstance.post(
    `/api/ai-engagements/${engagementId}/reports`,
    { report_key: reportKey, format },
    { responseType: 'blob', timeout: 300000 }
  );
  const disposition = resp.headers['content-disposition'] || '';
  const filename = (disposition.match(/filename="?([^";]+)"?/) || [])[1] || `${reportKey}.${format}`;
  const url = URL.createObjectURL(resp.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export async function getEngagementMessages(engagementId) {
  const resp = await axiosInstance.get(`/api/ai-engagements/${engagementId}/messages`);
  return resp.data?.messages || [];
}

export async function getEngagementResults(engagementId) {
  const resp = await axiosInstance.get(`/api/ai-engagements/${engagementId}/results`);
  return resp.data?.results || [];
}

async function streamEngagementEndpoint(url, body, onProgress, signal) {
  const response = await axiosInstance.post(url, body, {
    adapter: 'fetch', responseType: 'stream', validateStatus: () => true, timeout: 1200000, signal,
  });

  if (response.status < 200 || response.status >= 300) {
    let message = `HTTP error! status: ${response.status}`;
    try {
      const text = await new Response(response.data).text();
      const parsed = JSON.parse(text);
      message = parsed.message || parsed.error || message;
    } catch { /* keep default */ }
    throw new Error(message);
  }

  const reader = response.data.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let completeEvent = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const normalized = buffer.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const events = normalized.split('\n\n');
    buffer = normalized.endsWith('\n\n') ? '' : events.pop() || '';

    for (const eventText of events.filter((e) => e.trim())) {
      let eventType = 'message';
      let data = null;
      for (const line of eventText.split('\n')) {
        if (line.startsWith('event: ')) eventType = line.slice(7).trim();
        else if (line.startsWith('data: ')) {
          try { data = JSON.parse(line.slice(6)); } catch { /* ignore partial */ }
        }
      }
      if (!data) continue;
      if (eventType === 'complete' || data.stage === 'complete') {
        completeEvent = data;
      } else if (eventType === 'error' || data.stage === 'error') {
        throw new Error(data.error || data.message || 'Engagement request failed');
      } else if (onProgress) {
        onProgress(data);
      }
    }
  }

  return completeEvent;
}

/**
 * Run the initial AI assessment via SSE. The backend builds the authoritative
 * payload (control info + documents + context) server-side; the body only
 * carries optional free-text context. Same hand-rolled SSE protocol as
 * runAttributeTest.js.
 */
export async function runEngagementAnalysis({ engagementId, additionalContext, onProgress, signal }) {
  const complete = await streamEngagementEndpoint(
    `/api/ai-engagements/${engagementId}/analyze-stream`,
    { additional_context: additionalContext || '' },
    onProgress,
    signal
  );
  return { result: complete?.result || null, versionNumber: complete?.version_number ?? null };
}

/**
 * Send one conversational turn. Resolves with the assistant's reply plus the
 * turn classification (answer | revised_assessment | needs_documents |
 * rerun_requested) and, for revisions, the new result version.
 */
export async function sendEngagementChat({ engagementId, message, onProgress, signal }) {
  const complete = await streamEngagementEndpoint(
    `/api/ai-engagements/${engagementId}/chat-stream`,
    { message },
    onProgress,
    signal
  );
  return {
    message: complete?.message || '',
    responseType: complete?.response_type || 'answer',
    result: complete?.result || null,
    versionNumber: complete?.version_number ?? null,
  };
}
